import React, { useEffect, useMemo, useState } from 'react';
import {
  Breadcrumb,
  Button,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../utils/feedback';
import type { CustomerOptionDto } from '../../dto/reference';
import { matchesCustomerPickerSearch } from '../../modules/reference-data/customerSearch';
import './CustomerPickerModal.css';

const { Text } = Typography;
const UNCATEGORIZED_KEY = '__uncategorized__';

interface CustomerCategoryGroup {
  name: string;
  color?: string | null;
  customers: CustomerOptionDto[];
}

interface CustomerPickerModalProps {
  open: boolean;
  customers: CustomerOptionDto[];
  loading?: boolean;
  selectedCustomerId?: string | null;
  onSelect: (customer: CustomerOptionDto) => void;
  onRefresh: () => Promise<unknown>;
  onCancel: () => void;
}

const getCategoryKey = (customer: CustomerOptionDto) => customer.customerCategoryId || UNCATEGORIZED_KEY;

const CustomerPickerModal: React.FC<CustomerPickerModalProps> = ({
  open,
  customers,
  loading = false,
  selectedCustomerId,
  onSelect,
  onRefresh,
  onCancel,
}) => {
  const { t } = useTranslation(['customers', 'common']);
  const feedback = useFeedback();
  const [search, setSearch] = useState('');
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedCategoryKey(undefined);
  }, [open]);

  const categories = useMemo(() => {
    const grouped = new Map<string, CustomerCategoryGroup>();
    customers.forEach((customer) => {
      const key = getCategoryKey(customer);
      const existing = grouped.get(key);
      if (existing) {
        existing.customers.push(customer);
      } else {
        grouped.set(key, {
          name: customer.customerCategoryName || t('customers:customerCategory.none'),
          color: customer.customerCategoryColor,
          customers: [customer],
        });
      }
    });
    return Array.from(grouped.entries()).sort(([, left], [, right]) => (
      left.name.localeCompare(right.name, 'pl')
    ));
  }, [customers, t]);

  const selectedCategory = categories.find(([key]) => key === selectedCategoryKey);

  const searchResults = useMemo(() => {
    const query = search.trim();
    if (!query) return [];
    return customers.filter((customer) => matchesCustomerPickerSearch(
      { name: customer.name, taxId: customer.taxId, parentName: null },
      query,
    ) || customer.searchText.includes(query.trim().toLowerCase()));
  }, [customers, search]);

  const handleRefresh = async () => {
    try {
      await onRefresh();
      feedback.success(t('customers:customerPicker.refreshSuccess'));
    } catch {
      feedback.error(t('customers:customerPicker.refreshFailed'));
    }
  };

  const buildCustomerMeta = (customer: CustomerOptionDto) => (
    [
      customer.taxId ? `NIP ${customer.taxId}` : null,
      customer.address,
    ].filter(Boolean).join(' · ')
  );

  const renderCustomerRow = (customer: CustomerOptionDto, breadcrumb?: string) => (
    <button
      key={customer.id}
      type="button"
      className={`customer-picker-modal__row${customer.id === selectedCustomerId ? ' is-selected' : ''}`}
      onClick={() => onSelect(customer)}
    >
      <span className="customer-picker-modal__row-main">
        <span className="customer-picker-modal__row-title">
          <strong>{customer.name}</strong>
          {customer.isBranch && <Tag bordered={false}>{t('customers:preview.branch')}</Tag>}
        </span>
        {breadcrumb && <span className="customer-picker-modal__breadcrumb-text">{breadcrumb}</span>}
        {buildCustomerMeta(customer) && (
          <Text type="secondary" className="customer-picker-modal__meta">
            {buildCustomerMeta(customer)}
          </Text>
        )}
        {customer.customerCategoryName && (
          <Tag color={customer.customerCategoryColor || 'default'}>
            {customer.customerCategoryName}
          </Tag>
        )}
      </span>
      <RightOutlined aria-hidden="true" />
    </button>
  );

  const renderSearchResults = () => (
    <div className="customer-picker-modal__list" role="list">
      {searchResults.map((customer) => renderCustomerRow(
        customer,
        [
          customer.customerCategoryName || t('customers:customerCategory.none'),
          customer.name,
        ].filter(Boolean).join(' → '),
      ))}
      {searchResults.length === 0 && !loading && (
        <Empty description={t('customers:customerPicker.noResults')} />
      )}
    </div>
  );

  const renderCategories = () => (
    <div className="customer-picker-modal__grid" role="list">
      {categories.map(([key, category]) => (
        <button
          key={key}
          type="button"
          className="customer-picker-modal__category"
          onClick={() => setSelectedCategoryKey(key)}
        >
          <span
            className="customer-picker-modal__category-dot"
            style={{ backgroundColor: category.color || '#94a3b8' }}
            aria-hidden="true"
          />
          <span className="customer-picker-modal__row-main">
            <strong>{category.name}</strong>
            <Text type="secondary">
              {t('customers:customerPicker.categoryCustomerCount', { count: category.customers.length })}
            </Text>
          </span>
          <RightOutlined aria-hidden="true" />
        </button>
      ))}
      {categories.length === 0 && !loading && (
        <Empty description={t('customers:customerPicker.noCategories')} />
      )}
    </div>
  );

  const renderCustomers = () => (
    <div className="customer-picker-modal__list" role="list">
      {(selectedCategory?.[1].customers || []).map((customer) => renderCustomerRow(customer))}
      {(selectedCategory?.[1].customers || []).length === 0 && !loading && (
        <Empty description={t('customers:customerPicker.empty')} />
      )}
    </div>
  );

  const isSearching = Boolean(search.trim());

  // The modal is an ephemeral picker: there is no form state to preserve
  // between openings. Rendering `null` as soon as it closes deliberately
  // avoids Ant Design's exit-motion wrapper staying above the page when the
  // browser throttles CSS animations in an inactive tab.
  if (!open) return null;

  return (
    <Modal
      title={t('customers:customerPicker.modalTitle')}
      open={open}
      onCancel={onCancel}
      footer={(
        <div className="customer-picker-modal__footer">
          <Button
            className="customer-picker-modal__refresh"
            icon={<ReloadOutlined aria-hidden="true" />}
            loading={loading}
            onClick={handleRefresh}
          >
            {t('customers:customerPicker.refresh')}
          </Button>
          <Button onClick={onCancel}>{t('common:cancel')}</Button>
        </div>
      )}
      width={760}
    >
      <Input
        autoFocus
        allowClear
        prefix={<SearchOutlined aria-hidden="true" />}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('customers:customerPicker.searchPlaceholder')}
        aria-label={t('customers:customerPicker.searchLabel')}
      />

      {!isSearching && (
        <div className="customer-picker-modal__navigation">
          {selectedCategoryKey && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => setSelectedCategoryKey(undefined)}
            >
              {t('common:back')}
            </Button>
          )}
          <Breadcrumb
            items={[
              { title: t('customers:customerPicker.categoryStep') },
              ...(selectedCategory ? [{ title: selectedCategory[1].name }] : []),
            ]}
          />
        </div>
      )}

      <Spin spinning={loading}>
        <div className="customer-picker-modal__content">
          {isSearching
            ? renderSearchResults()
            : !selectedCategoryKey
              ? renderCategories()
              : renderCustomers()}
        </div>
      </Spin>
    </Modal>
  );
};

export default CustomerPickerModal;
